import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Locations() {
  const { withAuth } = useAuth();
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    withAuth(async () => {
      const [groupRes, locRes] = await Promise.all([
        api.getLocationGroups(),
        api.getLocations(),
      ]);
      const g = groupRes.data || [];
      const l = locRes.data || [];
      setGroups(g);
      setLocations(l);
      // Open all groups by default
      const open = {};
      g.forEach(grp => { open[grp.id] = true; });
      setOpenGroups(open);
    }).finally(() => setLoading(false));
  }, [withAuth]);

  const toggle = id => setOpenGroups(o => ({ ...o, [id]: !o[id] }));

  const filtered = search
    ? locations.filter(l =>
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.city?.toLowerCase().includes(search.toLowerCase()) ||
        l.workday_id?.toLowerCase().includes(search.toLowerCase())
      )
    : locations;

  const ungrouped = filtered.filter(l => !l.hierarchy_group_id);
  const byGroup = groups.map(g => ({
    ...g,
    sites: filtered.filter(l => l.hierarchy_group_id === g.id),
  })).filter(g => g.sites.length > 0);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>Location Hierarchy</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>{locations.length} active sites · {groups.length} groups</p>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-2)', background: 'var(--bg-3)', padding: '4px 10px', borderRadius: 'var(--radius-sm)' }}>
          Groups used for access control scoping
        </span>
      </div>

      <div style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)' }}>
        <input
          placeholder="Search locations…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
      </div>

      <div style={{ padding: '20px 28px' }}>
        {byGroup.map(g => (
          <div key={g.id} style={{ marginBottom: 12 }}>
            <div
              onClick={() => toggle(g.id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px', background: 'var(--bg-2)',
                borderRadius: openGroups[g.id] ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)',
                cursor: 'pointer', border: '1px solid var(--border)',
                borderBottom: openGroups[g.id] ? 'none' : '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{openGroups[g.id] ? '▾' : '▸'}</span>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{g.name}</span>
                <span className="badge badge-gray">{g.sites.length} sites</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  {g.sites.reduce((s, l) => s + parseInt(l.asset_count || 0), 0)} assets
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  {g.sites.reduce((s, l) => s + parseInt(l.employee_count || 0), 0)} staff
                </span>
              </div>
            </div>

            {openGroups[g.id] && (
              <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius-md) var(--radius-md)', overflow: 'hidden' }}>
                {g.sites.map((l, i) => (
                  <div key={l.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 20px',
                    borderBottom: i < g.sites.length - 1 ? '1px solid var(--border)' : 'none',
                    background: 'var(--bg-1)',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{l.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 1 }}>
                        <span className="mono" style={{ fontSize: 12 }}>{l.workday_id}</span>
                        {l.city && <> · {l.city}</>}
                        {l.address && <> · {l.address}</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {parseInt(l.asset_count) > 0 && (
                        <span className="badge badge-blue">{l.asset_count} assets</span>
                      )}
                      {parseInt(l.employee_count) > 0 && (
                        <span className="badge badge-gray">{l.employee_count} staff</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {ungrouped.length > 0 && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, marginTop: 8 }}>
              Ungrouped Sites ({ungrouped.length})
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              {ungrouped.map((l, i) => (
                <div key={l.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 20px',
                  borderBottom: i < ungrouped.length - 1 ? '1px solid var(--border)' : 'none',
                  background: 'var(--bg-1)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{l.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 1 }}>
                      <span className="mono" style={{ fontSize: 12 }}>{l.workday_id}</span>
                      {l.city && <> · {l.city}</>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {parseInt(l.asset_count) > 0 && <span className="badge badge-blue">{l.asset_count} assets</span>}
                    {parseInt(l.employee_count) > 0 && <span className="badge badge-gray">{l.employee_count} staff</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
